import React, { useEffect, useMemo, useState } from "react";
import {
  Text,
  Badge,
  Modal,
} from "@shopify/polaris";
import { isPendingReturnOrder } from "../utils/rentalStatus";
import LicenseExpiredOverlay from "../components/LicenseExpiredOverlay";

const FILTER_STATUS_OPTIONS = [
  { label: "All Statuses", value: "all" },
  { label: "On Rent", value: "On Rent" },
  { label: "Pending Approval", value: "Return Requested" },
  { label: "Pending Fulfillment", value: "Reserved" },
  { label: "Closed / Returned", value: "Closed" },
];

function normalizeDateKey(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateOnly) {
    return `${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}`;
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function displayStatusLabel(status) {
  const value = String(status || "").trim();
  if (!value) return "Pending Fulfillment";
  if (value.toLowerCase() === "reserved") return "Pending Fulfillment";
  if (value.toLowerCase() === "return") return "Closed";
  return value;
}

function getRentalStageLabel(order = {}) {
  const rawStatus = String(order.status || order.statusLabel || "").trim().toLowerCase();
  const startKey = normalizeDateKey(order.startDate);
  const endKey = normalizeDateKey(order.endDate);
  const todayKey = normalizeDateKey(new Date());

  if (rawStatus === "return requested") return "Return Requested";
  if (rawStatus === "return" || rawStatus === "closed") return "Closed";
  if (rawStatus === "rejected") return "Rejected";
  if (rawStatus === "on rent") return "On Rent";

  if (startKey && endKey && todayKey && todayKey >= startKey && todayKey <= endKey) {
    return "On Rent";
  }
  if (endKey && todayKey && todayKey > endKey) return "Closed";
  if (startKey && todayKey && todayKey < startKey) return "Pending Fulfillment";
  if (rawStatus === "reserved") return "Pending Fulfillment";
  return displayStatusLabel(order.status || order.statusLabel || "Pending Fulfillment");
}

function formatDateDisplay(value) {
  if (!value) return "-";
  const dateKey = normalizeDateKey(value);
  if (!dateKey) return String(value);
  const parts = dateKey.split("-");
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatShortDate(value) {
  if (!value) return "-";
  const dateKey = normalizeDateKey(value);
  if (!dateKey) return String(value);
  const parts = dateKey.split("-");
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function getDaysDifference(startKey, endKey) {
  if (!startKey || !endKey) return 0;
  const start = new Date(startKey).getTime();
  const end = new Date(endKey).getTime();
  if (Number.isNaN(start) || Number.isNaN(end)) return 0;
  const diff = Math.round((end - start) / (1000 * 60 * 60 * 24));
  return Math.max(1, diff + 1); // inclusive days
}

function isOrderActiveOnDate(order, targetDateKey) {
  if (!targetDateKey) return false;
  const startKey = normalizeDateKey(order.startDate);
  const endKey = normalizeDateKey(order.endDate);

  if (startKey && endKey) {
    return targetDateKey >= startKey && targetDateKey <= endKey;
  }
  if (startKey) {
    return targetDateKey === startKey;
  }
  if (endKey) {
    return targetDateKey === endKey;
  }
  return false;
}

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const WEEKDAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Styles
const PAGE_SHELL_STYLE = {
  maxWidth: "1560px",
  margin: "0 auto",
  padding: "10px 16px 36px",
};

const HERO_STYLE = {
  padding: "26px 30px 22px",
  borderRadius: "18px",
  background: "linear-gradient(180deg, #0b1a39 0%, #0e2149 100%)",
  boxShadow: "0 18px 44px rgba(7, 15, 31, 0.18)",
  overflow: "hidden",
  marginBottom: "20px",
};

const HERO_TOP_STYLE = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
};

const SUMMARY_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "14px",
  marginTop: "20px",
};

const SUMMARY_TILE_STYLE = {
  padding: "18px 20px",
  borderRadius: "14px",
  background: "#ffffff",
  boxShadow: "0 6px 18px rgba(10, 18, 38, 0.08)",
  display: "flex",
  alignItems: "center",
  gap: "14px",
};

const KPI_ICON_STYLE = {
  width: "48px",
  height: "48px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  fontSize: "20px",
};

const CONTROL_BAR_STYLE = {
  background: "#ffffff",
  borderRadius: "14px",
  padding: "16px 20px",
  boxShadow: "0 6px 20px rgba(10, 18, 38, 0.06)",
  border: "1px solid #e2e8f0",
  marginBottom: "20px",
};

const HERO_ACTION_BUTTON_STYLE = {
  appearance: "none",
  border: "1px solid rgba(255,255,255,0.26)",
  borderRadius: "10px",
  padding: "0 18px",
  minHeight: "44px",
  fontSize: "14px",
  fontWeight: 700,
  color: "#ffffff",
  background: "transparent",
  cursor: "pointer",
  boxShadow: "0 1px 0 rgba(255,255,255,0.08) inset",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
};

const HERO_REFRESH_BUTTON_STYLE = {
  ...HERO_ACTION_BUTTON_STYLE,
  borderColor: "#2f74ff",
  background: "#2f74ff",
  boxShadow: "0 8px 20px rgba(47, 116, 255, 0.28)",
};

const CALENDAR_CONTAINER_STYLE = {
  background: "#ffffff",
  borderRadius: "16px",
  border: "1px solid #e2e8f0",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
  overflow: "hidden",
  marginBottom: "24px",
};

const CALENDAR_HEADER_STYLE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "20px 24px",
  borderBottom: "1px solid #edf2f7",
  flexWrap: "wrap",
  gap: "12px",
};

const CALENDAR_NAV_BTN_STYLE = {
  appearance: "none",
  border: "1px solid #cbd5e1",
  background: "#ffffff",
  color: "#1e293b",
  borderRadius: "8px",
  padding: "8px 14px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
  transition: "all 0.15s ease",
};

const CALENDAR_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  background: "#f1f5f9",
  gap: "1px",
  borderBottom: "1px solid #e2e8f0",
};

const WEEKDAY_HEADER_STYLE = {
  background: "#f8fafc",
  padding: "12px 8px",
  textAlign: "center",
  fontWeight: 700,
  fontSize: "13px",
  color: "#475569",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const DAY_CELL_STYLE = {
  background: "#ffffff",
  minHeight: "116px",
  padding: "8px 10px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  cursor: "pointer",
  transition: "background-color 0.15s ease, box-shadow 0.15s ease",
  position: "relative",
};

const DAY_CELL_SELECTED_STYLE = {
  ...DAY_CELL_STYLE,
  background: "#eff6ff",
  boxShadow: "inset 0 0 0 2px #2563eb",
};

const DAY_CELL_TODAY_STYLE = {
  ...DAY_CELL_STYLE,
  background: "#f8fafc",
};

const DAY_CELL_OTHER_MONTH_STYLE = {
  ...DAY_CELL_STYLE,
  background: "#fafbfc",
  opacity: 0.65,
};

const CHIP_BASE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
  padding: "2px 6px",
  borderRadius: "6px",
  fontSize: "11px",
  fontWeight: 700,
  lineHeight: 1.2,
  marginBottom: "3px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
};

const ORDER_CARD_STYLE = {
  background: "#ffffff",
  borderRadius: "14px",
  border: "1px solid #e2e8f0",
  padding: "18px 20px",
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
  transition: "transform 0.12s ease, box-shadow 0.12s ease",
};

const ORDER_CARD_GRID_STYLE = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
  gap: "16px",
};

const MODAL_SECTION_STYLE = {
  display: "grid",
  gap: "12px",
  padding: "14px 16px 16px",
  borderRadius: "14px",
  border: "1px solid #e5eaf2",
  background: "#ffffff",
};

export default function RentalCalendar({ onNavigate, license }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [licenseExpired, setLicenseExpired] = useState(false);

  const isLicenseFlaggedExpired =
    Boolean(license?.remainingDays !== undefined && Number(license.remainingDays) <= 0) ||
    Boolean(license?.plan_active === false);

  const isExpired = licenseExpired || isLicenseFlaggedExpired;

  // Calendar View State
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [selectedDateKey, setSelectedDateKey] = useState(() => normalizeDateKey(new Date()));
  const [filterMode, setFilterMode] = useState("selected_date"); // "selected_date", "all_month", "all_time"
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewFormat, setViewFormat] = useState("calendar"); // "calendar" | "list"

  // Quick View Modal State
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const [quickViewData, setQuickViewData] = useState(null);

  // Load orders
  async function loadOrders(activeRef = { current: true }, options = {}) {
    if (activeRef.current) {
      setLoading(true);
      setError("");
    }

    try {
      const params = new URLSearchParams();
      if (options?.sync) params.set("sync", "latest");
      params.set("_ts", String(Date.now()));
      const response = await fetch(`/api/admin/rental-orders?${params.toString()}`, {
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403 || data?.isExpired || String(data?.error || "").toLowerCase().includes("license expired")) {
          setLicenseExpired(true);
        }
        throw new Error(data?.message || data?.error || "Failed to load rental orders");
      }

      if (!activeRef.current) return;
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      if (!activeRef.current) return;
      const errMsg = err?.message || "Failed to load rental orders";
      if (errMsg.toLowerCase().includes("license expired") || errMsg.toLowerCase().includes("license")) {
        setLicenseExpired(true);
      }
      setError(errMsg);
    } finally {
      if (activeRef.current) setLoading(false);
    }
  }

  useEffect(() => {
    const activeRef = { current: true };
    loadOrders(activeRef, { sync: true });
    return () => {
      activeRef.current = false;
    };
  }, []);

  const syncHistoricOrders = async () => {
    setSyncing(true);
    setError("");
    setSuccessMessage("");

    try {
      const response = await fetch("/api/admin/rental-orders/sync", {
        method: "POST",
        credentials: "include",
        cache: "no-store",
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to sync older rental orders");
      }

      setSuccessMessage(`Synced ${Number(data?.synced) || 0} rental orders from Shopify.`);
      await loadOrders();
    } catch (err) {
      setError(err?.message || "Failed to sync older rental orders");
    } finally {
      setSyncing(false);
    }
  };

  // Year and Month
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0 - 11
  const todayKey = normalizeDateKey(new Date());

  // Navigation handlers
  const handlePrevMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const handleGoToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDateKey(normalizeDateKey(today));
    setFilterMode("selected_date");
  };

  const handleYearChange = (year) => {
    setCurrentDate((prev) => new Date(Number(year), prev.getMonth(), 1));
  };

  const handleMonthChange = (monthIdx) => {
    setCurrentDate((prev) => new Date(prev.getFullYear(), Number(monthIdx), 1));
  };

  // Calendar Grid Builder
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);

    const startDayOfWeek = firstDayOfMonth.getDay(); // 0 (Sun) to 6 (Sat)
    const daysInCurrentMonth = lastDayOfMonth.getDate();

    const days = [];

    // Previous month padding days
    const prevMonthLastDay = new Date(currentYear, currentMonth, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const dayNum = prevMonthLastDay - i;
      const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      const dateKey = `${prevYear}-${String(prevMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
      days.push({
        dayNumber: dayNum,
        dateKey,
        isCurrentMonth: false,
        date: new Date(prevYear, prevMonth, dayNum),
      });
    }

    // Current month days
    for (let dayNum = 1; dayNum <= daysInCurrentMonth; dayNum++) {
      const dateKey = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
      days.push({
        dayNumber: dayNum,
        dateKey,
        isCurrentMonth: true,
        date: new Date(currentYear, currentMonth, dayNum),
      });
    }

    // Next month padding days to complete 35 or 42 grid cells
    const totalCells = days.length > 35 ? 42 : 35;
    const remaining = totalCells - days.length;
    for (let dayNum = 1; dayNum <= remaining; dayNum++) {
      const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
      const nextYear = currentMonth === 11 ? currentYear + 1 : currentYear;
      const dateKey = `${nextYear}-${String(nextMonth + 1).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
      days.push({
        dayNumber: dayNum,
        dateKey,
        isCurrentMonth: false,
        date: new Date(nextYear, nextMonth, dayNum),
      });
    }

    return days;
  }, [currentYear, currentMonth]);

  // Map orders by date for quick calendar lookups
  const ordersByDate = useMemo(() => {
    const map = {};

    orders.forEach((order) => {
      const stage = getRentalStageLabel(order);
      const startKey = normalizeDateKey(order.startDate);
      const endKey = normalizeDateKey(order.endDate);

      if (!startKey && !endKey) return;

      const effStart = startKey || endKey;
      const effEnd = endKey || startKey;

      // Iterate through range
      let cur = new Date(effStart);
      const end = new Date(effEnd);

      // Limit iteration to prevent infinite loop
      let count = 0;
      while (cur <= end && count < 366) {
        const dateKey = normalizeDateKey(cur);
        if (!map[dateKey]) {
          map[dateKey] = {
            running: [],
            pendingFulfillment: [],
            pendingApproval: [],
            closed: [],
            all: [],
          };
        }

        map[dateKey].all.push(order);

        if (stage === "On Rent") {
          map[dateKey].running.push(order);
        } else if (stage === "Return Requested" || isPendingReturnOrder(order)) {
          map[dateKey].pendingApproval.push(order);
        } else if (stage === "Pending Fulfillment" || stage === "Reserved") {
          map[dateKey].pendingFulfillment.push(order);
        } else if (stage === "Closed" || stage === "Return") {
          map[dateKey].closed.push(order);
        } else {
          map[dateKey].running.push(order);
        }

        cur.setDate(cur.getDate() + 1);
        count++;
      }
    });

    return map;
  }, [orders]);

  // Filtered orders list for the breakdown panel
  const filteredOrders = useMemo(() => {
    const query = String(searchTerm || "").trim().toLowerCase();

    return orders.filter((order) => {
      // 1. Search filter
      const matchesSearch =
        !query ||
        String(order.orderId || "").toLowerCase().includes(query) ||
        String(order.orderLabel || "").toLowerCase().includes(query) ||
        String(order.productName || "").toLowerCase().includes(query) ||
        String(order.customerName || "").toLowerCase().includes(query) ||
        String(order.customerEmail || "").toLowerCase().includes(query);

      if (!matchesSearch) return false;

      // 2. Stage/Status filter
      const stage = getRentalStageLabel(order);
      let matchesStatus = true;
      if (statusFilter === "On Rent") {
        matchesStatus = stage === "On Rent";
      } else if (statusFilter === "Return Requested") {
        matchesStatus = stage === "Return Requested" || isPendingReturnOrder(order);
      } else if (statusFilter === "Reserved") {
        matchesStatus = stage === "Pending Fulfillment";
      } else if (statusFilter === "Closed") {
        matchesStatus = stage === "Closed" || stage === "Return";
      }

      if (!matchesStatus) return false;

      // 3. Date Mode filter
      if (filterMode === "selected_date") {
        if (!selectedDateKey) return true;
        return isOrderActiveOnDate(order, selectedDateKey);
      } else if (filterMode === "all_month") {
        const monthPrefix = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}`;
        const startKey = normalizeDateKey(order.startDate);
        const endKey = normalizeDateKey(order.endDate);
        const startsInMonth = startKey && startKey.startsWith(monthPrefix);
        const endsInMonth = endKey && endKey.startsWith(monthPrefix);
        const spansMonth =
          startKey && endKey && startKey <= `${monthPrefix}-31` && endKey >= `${monthPrefix}-01`;
        return startsInMonth || endsInMonth || spansMonth;
      }

      return true; // "all_time"
    });
  }, [orders, searchTerm, statusFilter, filterMode, selectedDateKey, currentYear, currentMonth]);

  // Overall & Contextual Metrics
  const metrics = useMemo(() => {
    const targetOrders = filterMode === "all_time" ? orders : filteredOrders;

    return targetOrders.reduce(
      (acc, order) => {
        acc.total += 1;
        const stage = getRentalStageLabel(order);
        const qty = Number(order.totalQuantity ?? order.quantity) || 1;
        acc.totalUnits += qty;

        if (stage === "On Rent") {
          acc.running += 1;
          acc.runningUnits += qty;
        } else if (stage === "Return Requested" || isPendingReturnOrder(order)) {
          acc.pendingApproval += 1;
        } else if (stage === "Pending Fulfillment" || stage === "Reserved") {
          acc.pendingFulfillment += 1;
        } else if (stage === "Closed" || stage === "Return") {
          acc.closed += 1;
        } else {
          acc.running += 1;
        }

        return acc;
      },
      {
        total: 0,
        running: 0,
        pendingApproval: 0,
        pendingFulfillment: 0,
        closed: 0,
        totalUnits: 0,
        runningUnits: 0,
      },
    );
  }, [orders, filteredOrders, filterMode]);

  // Quick view detail modal opener
  const openQuickView = async (order) => {
    const orderId = String(order?.orderId || "").trim();
    if (!orderId) return;

    const initialPhone = String(
      order?.customerPhone ||
      order?.phone ||
      order?.customer?.phone ||
      order?.shippingAddress?.phone ||
      order?.billingAddress?.phone ||
      ""
    ).trim();

    const initialEmail = String(
      order?.customerEmail ||
      order?.email ||
      order?.customer?.email ||
      ""
    ).trim();

    const initialStartDate = order?.startDate || order?.start_date;
    const initialEndDate = order?.endDate || order?.end_date;

    setQuickViewOpen(true);
    setQuickViewLoading(true);
    setQuickViewData({
      orderId,
      orderLabel: order?.orderLabel || `#${orderId}`,
      productName: order?.productName || "-",
      customerName: order?.customerName || "Guest",
      customerEmail: initialEmail,
      customerPhone: initialPhone,
      status: order?.status || "Reserved",
      startDate: initialStartDate,
      endDate: initialEndDate,
      totalQuantity: Number(order?.totalQuantity ?? order?.quantity) || 1,
      rentalTotal: order?.rentalTotal || "$0.00",
      rentalLines: [],
      shippingAddressLines: [],
      billingAddressLines: [],
    });

    try {
      const response = await fetch(`/api/admin/rental-orders/${encodeURIComponent(orderId)}`, {
        credentials: "include",
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to load order details");
      }

      const firstLine = Array.isArray(data?.rentalLines) && data.rentalLines.length > 0 ? data.rentalLines[0] : {};

      const resolvedStartDate =
        data?.startDate ||
        data?.start_date ||
        firstLine?.startDate ||
        firstLine?.start_date ||
        initialStartDate;

      const resolvedEndDate =
        data?.endDate ||
        data?.end_date ||
        firstLine?.endDate ||
        firstLine?.end_date ||
        initialEndDate;

      const resolvedPhone = String(
        data?.customerPhone ||
        data?.phone ||
        data?.customer?.phone ||
        data?.shippingAddress?.phone ||
        data?.billingAddress?.phone ||
        data?.shipping_address?.phone ||
        data?.billing_address?.phone ||
        initialPhone ||
        ""
      ).trim();

      const resolvedEmail = String(
        data?.customerEmail ||
        data?.email ||
        data?.customer?.email ||
        data?.contact_email ||
        initialEmail ||
        ""
      ).trim();

      setQuickViewData({
        ...order,
        ...data,
        startDate: resolvedStartDate,
        endDate: resolvedEndDate,
        customerPhone: resolvedPhone,
        customerEmail: resolvedEmail,
        customerName: data?.customerName || order?.customerName || "Guest",
        productName: data?.productName || firstLine?.title || firstLine?.productName || order?.productName || "-",
        totalQuantity: Number(data?.totalQuantity ?? data?.quantity ?? firstLine?.quantity ?? order?.totalQuantity ?? order?.quantity) || 1,
      });
    } catch (err) {
      console.warn("Failed to load single order quick view:", err.message);
    } finally {
      setQuickViewLoading(false);
    }
  };

  const exportOrders = () => {
    const rows = filteredOrders.map((order) => ({
      order_id: order.orderId || "",
      order_name: order.orderLabel || "",
      product_name: order.productName || "",
      customer_name: order.customerName || "",
      start_date: order.startDate || "",
      end_date: order.endDate || "",
      status: getRentalStageLabel(order),
      rental_total: order.rentalTotal || "",
    }));

    if (!rows.length) return;

    const headers = Object.keys(rows[0]);
    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((header) => `"${String(row[header] ?? "").replace(/"/g, '""')}"`)
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rental-calendar-${selectedDateKey || "export"}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {isExpired && (
        <LicenseExpiredOverlay
          onNavigate={onNavigate}
          license={license}
          customMessage="Your NetScore Rental Management license has expired. The rental calendar view is currently paused."
        />
      )}
      <div
        style={{
          ...PAGE_SHELL_STYLE,
          filter: isExpired ? "blur(6px)" : "none",
          pointerEvents: isExpired ? "none" : "auto",
          userSelect: isExpired ? "none" : "auto",
          transition: "filter 0.2s ease",
        }}
      >
        {/* Hero Header */}
        <div style={HERO_STYLE}>
        <div style={HERO_TOP_STYLE}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
              <span
                style={{
                  background: "rgba(255, 255, 255, 0.12)",
                  color: "#ffffff",
                  padding: "4px 10px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                Calendar & Scheduling
              </span>
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: "13px" }}>
                {orders.length} total orders recorded
              </span>
            </div>
            <h1 style={{ margin: 0, fontSize: "26px", fontWeight: 800, color: "#ffffff" }}>
              Rental Order Calendar
            </h1>
            <p style={{ margin: "6px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "14px", maxWidth: "680px" }}>
              Track all scheduled, running, pending, and returned orders by date. Click any date on the calendar to filter orders and see day-by-day availability.
            </p>
          </div>

          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={exportOrders}
              style={HERO_ACTION_BUTTON_STYLE}
              title="Download orders list as CSV"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={syncHistoricOrders}
              disabled={syncing}
              style={HERO_REFRESH_BUTTON_STYLE}
            >
              {syncing ? "Syncing..." : "Sync from Shopify"}
            </button>
          </div>
        </div>

        {/* Top KPI Metrics Cards */}
        <div style={SUMMARY_GRID_STYLE}>
          {/* Total Orders Tile */}
          <div style={SUMMARY_TILE_STYLE}>
            <div style={{ ...KPI_ICON_STYLE, background: "#e0e7ff", color: "#3730a3" }}>
              📋
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 600, textTransform: "uppercase" }}>
                {filterMode === "selected_date" ? "Orders on Date" : "Orders (In View)"}
              </div>
              <div style={{ color: "#0f172a", fontSize: "22px", fontWeight: 800, lineHeight: 1.15, marginTop: "2px" }}>
                {metrics.total}
              </div>
            </div>
          </div>

          {/* Running / On Rent Tile */}
          <div style={SUMMARY_TILE_STYLE}>
            <div style={{ ...KPI_ICON_STYLE, background: "#fef3c7", color: "#b45309" }}>
              ⚡
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 600, textTransform: "uppercase" }}>
                On Rent
              </div>
              <div style={{ color: "#b45309", fontSize: "22px", fontWeight: 800, lineHeight: 1.15, marginTop: "2px" }}>
                {metrics.running}
              </div>
            </div>
          </div>

          {/* Pending Fulfillment Tile */}
          <div style={SUMMARY_TILE_STYLE}>
            <div style={{ ...KPI_ICON_STYLE, background: "#e0f2fe", color: "#0369a1" }}>
              📦
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 600, textTransform: "uppercase" }}>
                Pending Fulfillment
              </div>
              <div style={{ color: "#0369a1", fontSize: "22px", fontWeight: 800, lineHeight: 1.15, marginTop: "2px" }}>
                {metrics.pendingFulfillment}
              </div>
            </div>
          </div>

          {/* Pending Approval / Return Requested */}
          <div style={SUMMARY_TILE_STYLE}>
            <div style={{ ...KPI_ICON_STYLE, background: "#fee2e2", color: "#dc2626" }}>
              ⏳
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 600, textTransform: "uppercase" }}>
                Pending Approval
              </div>
              <div style={{ color: "#dc2626", fontSize: "22px", fontWeight: 800, lineHeight: 1.15, marginTop: "2px" }}>
                {metrics.pendingApproval}
              </div>
            </div>
          </div>

          {/* Closed / Returned */}
          <div style={SUMMARY_TILE_STYLE}>
            <div style={{ ...KPI_ICON_STYLE, background: "#dcfce7", color: "#15803d" }}>
              ✓
            </div>
            <div>
              <div style={{ color: "#64748b", fontSize: "12px", fontWeight: 600, textTransform: "uppercase" }}>
                Closed / Returned
              </div>
              <div style={{ color: "#15803d", fontSize: "22px", fontWeight: 800, lineHeight: 1.15, marginTop: "2px" }}>
                {metrics.closed}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div
          style={{
            padding: "12px 16px",
            background: "#fee2e2",
            color: "#991b1b",
            borderRadius: "10px",
            marginBottom: "16px",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          style={{
            padding: "12px 16px",
            background: "#dcfce7",
            color: "#166534",
            borderRadius: "10px",
            marginBottom: "16px",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          {successMessage}
        </div>
      )}

      {/* Search & Filter Toolbar */}
      <div style={CONTROL_BAR_STYLE}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(220px, 1.4fr) minmax(180px, 1fr) auto auto",
            gap: "12px",
            alignItems: "center",
          }}
        >
          {/* Search Box */}
          <div style={{ position: "relative" }}>
            <input
              type="text"
              placeholder="Search by Order #, Customer, Product..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                minHeight: "42px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                padding: "0 14px",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: "100%",
                minHeight: "42px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                padding: "0 12px",
                fontSize: "14px",
                background: "#ffffff",
                outline: "none",
                cursor: "pointer",
                boxSizing: "border-box",
              }}
            >
              {FILTER_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Date Scope Pills */}
          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
            <button
              type="button"
              onClick={() => setFilterMode("selected_date")}
              style={{
                ...CALENDAR_NAV_BTN_STYLE,
                background: filterMode === "selected_date" ? "#1e293b" : "#ffffff",
                color: filterMode === "selected_date" ? "#ffffff" : "#1e293b",
                borderColor: filterMode === "selected_date" ? "#1e293b" : "#cbd5e1",
              }}
            >
              Selected Date ({formatShortDate(selectedDateKey)})
            </button>
            <button
              type="button"
              onClick={() => setFilterMode("all_month")}
              style={{
                ...CALENDAR_NAV_BTN_STYLE,
                background: filterMode === "all_month" ? "#1e293b" : "#ffffff",
                color: filterMode === "all_month" ? "#ffffff" : "#1e293b",
                borderColor: filterMode === "all_month" ? "#1e293b" : "#cbd5e1",
              }}
            >
              This Month
            </button>
            <button
              type="button"
              onClick={() => setFilterMode("all_time")}
              style={{
                ...CALENDAR_NAV_BTN_STYLE,
                background: filterMode === "all_time" ? "#1e293b" : "#ffffff",
                color: filterMode === "all_time" ? "#ffffff" : "#1e293b",
                borderColor: filterMode === "all_time" ? "#1e293b" : "#cbd5e1",
              }}
            >
              All Time
            </button>
          </div>

          {/* View Toggle */}
          <div style={{ display: "flex", gap: "4px" }}>
            <button
              type="button"
              onClick={() => setViewFormat("calendar")}
              style={{
                ...CALENDAR_NAV_BTN_STYLE,
                background: viewFormat === "calendar" ? "#2563eb" : "#f1f5f9",
                color: viewFormat === "calendar" ? "#ffffff" : "#475569",
                borderColor: viewFormat === "calendar" ? "#2563eb" : "#cbd5e1",
              }}
              title="Show Month Calendar View"
            >
              📅 Calendar
            </button>
            <button
              type="button"
              onClick={() => setViewFormat("list")}
              style={{
                ...CALENDAR_NAV_BTN_STYLE,
                background: viewFormat === "list" ? "#2563eb" : "#f1f5f9",
                color: viewFormat === "list" ? "#ffffff" : "#475569",
                borderColor: viewFormat === "list" ? "#2563eb" : "#cbd5e1",
              }}
              title="Show Orders List View"
            >
              📋 List
            </button>
          </div>
        </div>
      </div>

      {/* Main Month Calendar Component */}
      {viewFormat === "calendar" && (
        <div style={CALENDAR_CONTAINER_STYLE}>
          {/* Month Header & Controls */}
          <div style={CALENDAR_HEADER_STYLE}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ display: "flex", gap: "6px" }}>
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  style={CALENDAR_NAV_BTN_STYLE}
                  title="Previous Month"
                >
                  ‹ Prev
                </button>
                <button
                  type="button"
                  onClick={handleGoToday}
                  style={CALENDAR_NAV_BTN_STYLE}
                  title="Go to Today"
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  style={CALENDAR_NAV_BTN_STYLE}
                  title="Next Month"
                >
                  Next ›
                </button>
              </div>

              {/* Month and Year Selection */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <select
                  value={currentMonth}
                  onChange={(e) => handleMonthChange(e.target.value)}
                  style={{
                    fontSize: "17px",
                    fontWeight: 800,
                    color: "#0f172a",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {MONTH_NAMES.map((name, idx) => (
                    <option key={name} value={idx}>
                      {name}
                    </option>
                  ))}
                </select>

                <select
                  value={currentYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                  style={{
                    fontSize: "17px",
                    fontWeight: 800,
                    color: "#0f172a",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map(
                    (yr) => (
                      <option key={yr} value={yr}>
                        {yr}
                      </option>
                    ),
                  )}
                </select>
              </div>
            </div>

            {/* Legend */}
            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", fontSize: "12px" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#eab308" }} />
                <span style={{ color: "#475569", fontWeight: 600 }}>On Rent</span>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#0284c7" }} />
                <span style={{ color: "#475569", fontWeight: 600 }}>Pending Fulfillment</span>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#dc2626" }} />
                <span style={{ color: "#475569", fontWeight: 600 }}>Pending Approval</span>
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: "#16a34a" }} />
                <span style={{ color: "#475569", fontWeight: 600 }}>Closed</span>
              </span>
            </div>
          </div>

          {/* Weekday Row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
            {WEEKDAY_NAMES.map((day) => (
              <div key={day} style={WEEKDAY_HEADER_STYLE}>
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Day Grid */}
          <div style={CALENDAR_GRID_STYLE}>
            {calendarDays.map((cell) => {
              const isSelected = cell.dateKey === selectedDateKey && filterMode === "selected_date";
              const isToday = cell.dateKey === todayKey;
              const dateStats = ordersByDate[cell.dateKey] || {
                running: [],
                pendingFulfillment: [],
                pendingApproval: [],
                closed: [],
                all: [],
              };

              let cellStyle = DAY_CELL_STYLE;
              if (isSelected) cellStyle = DAY_CELL_SELECTED_STYLE;
              else if (isToday) cellStyle = DAY_CELL_TODAY_STYLE;
              else if (!cell.isCurrentMonth) cellStyle = DAY_CELL_OTHER_MONTH_STYLE;

              return (
                <div
                  key={cell.dateKey}
                  style={cellStyle}
                  onClick={() => {
                    setSelectedDateKey(cell.dateKey);
                    setFilterMode("selected_date");
                  }}
                >
                  {/* Day Number Row */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "4px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: isToday || isSelected ? 800 : 600,
                        color: isSelected ? "#2563eb" : isToday ? "#0f172a" : cell.isCurrentMonth ? "#334155" : "#94a3b8",
                        background: isToday ? "#e2e8f0" : "transparent",
                        padding: isToday ? "2px 6px" : "0",
                        borderRadius: isToday ? "6px" : "0",
                      }}
                    >
                      {cell.dayNumber}
                    </span>

                    {isToday && (
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          color: "#2563eb",
                          textTransform: "uppercase",
                        }}
                      >
                        Today
                      </span>
                    )}
                  </div>

                  {/* Order Badges for this Day */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px", minHeight: "60px" }}>
                    {dateStats.running.length > 0 && (
                      <div
                        style={{
                          ...CHIP_BASE_STYLE,
                          background: "#fef3c7",
                          color: "#92400e",
                          border: "1px solid #fde68a",
                        }}
                        title={`${dateStats.running.length} order(s) on rent`}
                      >
                        ⚡ {dateStats.running.length} On Rent
                      </div>
                    )}

                    {dateStats.pendingFulfillment.length > 0 && (
                      <div
                        style={{
                          ...CHIP_BASE_STYLE,
                          background: "#e0f2fe",
                          color: "#0369a1",
                          border: "1px solid #bae6fd",
                        }}
                        title={`${dateStats.pendingFulfillment.length} order(s) pending fulfillment`}
                      >
                        📦 {dateStats.pendingFulfillment.length} Pending
                      </div>
                    )}

                    {dateStats.pendingApproval.length > 0 && (
                      <div
                        style={{
                          ...CHIP_BASE_STYLE,
                          background: "#fee2e2",
                          color: "#991b1b",
                          border: "1px solid #fca5a5",
                        }}
                        title={`${dateStats.pendingApproval.length} return request(s) awaiting approval`}
                      >
                        ⏳ {dateStats.pendingApproval.length} Approval
                      </div>
                    )}

                    {dateStats.closed.length > 0 && (
                      <div
                        style={{
                          ...CHIP_BASE_STYLE,
                          background: "#dcfce7",
                          color: "#166534",
                          border: "1px solid #bbf7d0",
                        }}
                        title={`${dateStats.closed.length} closed order(s)`}
                      >
                        ✓ {dateStats.closed.length} Closed
                      </div>
                    )}
                  </div>

                  {/* Total indicator if busy */}
                  {dateStats.all.length > 0 && (
                    <div style={{ textAlign: "right", marginTop: "auto" }}>
                      <span style={{ fontSize: "10px", color: "#64748b", fontWeight: 700 }}>
                        {dateStats.all.length} {dateStats.all.length === 1 ? "order" : "orders"}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Orders List Section Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#0f172a" }}>
            {filterMode === "selected_date"
              ? `Orders Active on ${formatDateDisplay(selectedDateKey)}`
              : filterMode === "all_month"
                ? `Orders for ${MONTH_NAMES[currentMonth]} ${currentYear}`
                : "All Rental Orders"}
          </h2>
          <span style={{ color: "#64748b", fontSize: "13px" }}>
            Showing {filteredOrders.length} {filteredOrders.length === 1 ? "rental order" : "rental orders"}
          </span>
        </div>

        {filterMode === "selected_date" && (
          <button
            type="button"
            onClick={() => setFilterMode("all_month")}
            style={{ ...CALENDAR_NAV_BTN_STYLE, background: "#f8fafc" }}
          >
            View All Month Orders ({MONTH_NAMES[currentMonth]})
          </button>
        )}
      </div>

      {/* Orders Grid / Cards */}
      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", background: "#ffffff", borderRadius: "14px" }}>
          <Text tone="subdued">Loading rental orders...</Text>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div
          style={{
            padding: "50px 20px",
            textAlign: "center",
            background: "#ffffff",
            borderRadius: "14px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <div style={{ fontSize: "36px", marginBottom: "8px" }}>📅</div>
          <h3 style={{ margin: "0 0 6px", fontSize: "16px", fontWeight: 700, color: "#1e293b" }}>
            No rental orders found for this date/filter
          </h3>
          <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: "14px" }}>
            {filterMode === "selected_date"
              ? `No active orders running or scheduled for ${formatDateDisplay(selectedDateKey)}.`
              : "Try adjusting your search keywords or status filter."}
          </p>
          <button
            type="button"
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setFilterMode("all_month");
            }}
            style={CALENDAR_NAV_BTN_STYLE}
          >
            Reset Filters & View Month
          </button>
        </div>
      ) : (
        <div style={ORDER_CARD_GRID_STYLE}>
          {filteredOrders.map((order) => {
            const stage = getRentalStageLabel(order);
            const startKey = normalizeDateKey(order.startDate);
            const endKey = normalizeDateKey(order.endDate);
            const totalDays = getDaysDifference(startKey, endKey);
            const isTodayCovered = startKey && endKey && todayKey >= startKey && todayKey <= endKey;

            let badgeTone = "info";
            if (stage === "On Rent") badgeTone = "attention";
            else if (stage === "Return Requested") badgeTone = "critical";
            else if (stage === "Closed") badgeTone = "success";

            return (
              <div key={order.orderId} style={ORDER_CARD_STYLE}>
                {/* Card Top */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "16px", fontWeight: 800, color: "#0f172a" }}>
                        {order.orderLabel || `#${order.orderId}`}
                      </span>
                      <Badge tone={badgeTone}>{stage}</Badge>
                    </div>
                    <div style={{ color: "#64748b", fontSize: "13px", marginTop: "2px" }}>
                      {order.customerName || "Guest"}
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "15px", fontWeight: 800, color: "#0f172a" }}>
                      {order.rentalTotal || "$0.00"}
                    </div>
                    <div style={{ color: "#64748b", fontSize: "12px" }}>
                      Qty: {Number(order.totalQuantity ?? order.quantity) || 1}
                    </div>
                  </div>
                </div>

                {/* Product Name */}
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#f8fafc",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#334155",
                    marginBottom: "12px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  📦 {order.productName || "Rental Item"}
                </div>

                {/* Rental Period */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "14px", fontSize: "12px" }}>
                  <div style={{ padding: "8px", background: "#f1f5f9", borderRadius: "6px" }}>
                    <div style={{ color: "#64748b", fontWeight: 600 }}>Start Date</div>
                    <div style={{ color: "#0f172a", fontWeight: 700, marginTop: "2px" }}>
                      {formatDateDisplay(order.startDate)}
                    </div>
                  </div>
                  <div style={{ padding: "8px", background: "#f1f5f9", borderRadius: "6px" }}>
                    <div style={{ color: "#64748b", fontWeight: 600 }}>End Date</div>
                    <div style={{ color: "#0f172a", fontWeight: 700, marginTop: "2px" }}>
                      {formatDateDisplay(order.endDate)}
                    </div>
                  </div>
                </div>

                {/* Duration & Progress */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", fontSize: "12px" }}>
                  <span style={{ color: "#64748b" }}>
                    Duration: <strong style={{ color: "#0f172a" }}>{totalDays} {totalDays === 1 ? "day" : "days"}</strong>
                  </span>
                  {isTodayCovered ? (
                    <span style={{ color: "#b45309", fontWeight: 700 }}>⚡ Active Today</span>
                  ) : stage === "Closed" ? (
                    <span style={{ color: "#16a34a", fontWeight: 700 }}>✓ Completed</span>
                  ) : (
                    <span style={{ color: "#0284c7", fontWeight: 700 }}>Scheduled</span>
                  )}
                </div>

                {/* Actions */}
                <div style={{ display: "flex", gap: "8px", marginTop: "auto" }}>
                  <button
                    type="button"
                    onClick={() => openQuickView(order)}
                    style={{
                      ...CALENDAR_NAV_BTN_STYLE,
                      width: "100%",
                      padding: "8px 12px",
                      background: "#f8fafc",
                    }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick View Order Details Modal */}
      <Modal
        open={quickViewOpen}
        onClose={() => setQuickViewOpen(false)}
        title={`Rental Order ${quickViewData?.orderLabel || quickViewData?.orderId || ""}`}
        primaryAction={{
          content: "Close",
          onAction: () => setQuickViewOpen(false),
        }}
      >
        <Modal.Section>
          {quickViewLoading ? (
            <div style={{ padding: "24px", textAlign: "center" }}>
              <Text tone="subdued">Loading order details...</Text>
            </div>
          ) : quickViewData ? (
            <div style={{ display: "grid", gap: "14px" }}>
              {/* Summary Section */}
              <div style={MODAL_SECTION_STYLE}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <Text variant="headingMd" as="h4">
                      {quickViewData.productName || "Rental Item"}
                    </Text>
                    <Text tone="subdued">Customer: {quickViewData.customerName || "Guest"}</Text>
                  </div>
                  <Badge tone={quickViewData.status === "On Rent" ? "attention" : "info"}>
                    {displayStatusLabel(quickViewData.status)}
                  </Badge>
                </div>
              </div>

              {/* Dates & Quantities */}
              <div style={MODAL_SECTION_STYLE}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px" }}>
                  <div>
                    <Text tone="subdued" variant="bodySm">
                      Start Date
                    </Text>
                    <Text variant="bodyMd" fontWeight="bold">
                      {formatDateDisplay(quickViewData.startDate)}
                    </Text>
                  </div>
                  <div>
                    <Text tone="subdued" variant="bodySm">
                      End Date
                    </Text>
                    <Text variant="bodyMd" fontWeight="bold">
                      {formatDateDisplay(quickViewData.endDate)}
                    </Text>
                  </div>
                  <div>
                    <Text tone="subdued" variant="bodySm">
                      Total Quantity
                    </Text>
                    <Text variant="bodyMd" fontWeight="bold">
                      {Number(quickViewData.totalQuantity ?? quickViewData.quantity) || 1}
                    </Text>
                  </div>
                </div>
              </div>

              {/* Customer Contact */}
              <div style={MODAL_SECTION_STYLE}>
                <Text variant="headingSm" as="h5">
                  Customer Information
                </Text>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginTop: "6px" }}>
                  <div>
                    <Text tone="subdued" variant="bodySm">Email</Text>
                    <Text variant="bodyMd">{quickViewData.customerEmail || "-"}</Text>
                  </div>
                  <div>
                    <Text tone="subdued" variant="bodySm">Phone</Text>
                    <Text variant="bodyMd">{quickViewData.customerPhone || "-"}</Text>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </Modal.Section>
      </Modal>
    </div>
    </>
  );
}
